<!--
  @component AuditDetail
  @description Detalle de caso de auditoría: info del caso, bitácora de dictámenes,
               interconsultas, asignación de especialidad y resolución final.
-->
<template>
  <div class="audit-detail" v-loading="isLoading">
    <div
      class="audit-detail__back"
      @click="$router.push({ name: 'audit-list' })"
    >
      <i class="el-icon-arrow-left"></i>
      <span>Auditoría</span>
    </div>

    <template v-if="auditCase">
      <!-- Header -->
      <div class="audit-detail__header">
        <div class="audit-detail__header-left">
          <span class="audit-detail__case-num"
            >Auth #{{ auditCase.authCaseNumber }}</span
          >
          <span
            class="audit-detail__badge"
            :class="statusClass(auditCase.status)"
          >
            {{ statusLabel(auditCase.status) }}
          </span>
        </div>
        <span class="audit-detail__date">{{
          auditCase.createdAt | formatDate
        }}</span>
      </div>

      <!-- Info cards -->
      <div class="audit-detail__cards">
        <!-- Afiliado -->
        <div class="audit-detail__card">
          <div class="audit-detail__card-title">AFILIADO</div>
          <div class="audit-detail__row">
            <span>Nombre</span>
            <strong
              >{{ auditCase.memberSnapshot.lastName }},
              {{ auditCase.memberSnapshot.firstName }}</strong
            >
          </div>
          <div class="audit-detail__row">
            <span>N° Afiliado</span>
            <span>{{ auditCase.memberSnapshot.memberId || "-" }}</span>
          </div>
          <div class="audit-detail__row">
            <span>Plan</span>
            <span class="audit-detail__plan">{{
              auditCase.memberSnapshot.planName || "-"
            }}</span>
          </div>
        </div>

        <!-- Prestación -->
        <div class="audit-detail__card">
          <div class="audit-detail__card-title">PRESTACIÓN</div>
          <div class="audit-detail__row">
            <span>Código</span>
            <strong class="audit-detail__code">{{
              auditCase.practiceSnapshot.code
            }}</strong>
          </div>
          <div class="audit-detail__row">
            <span>Nombre</span>
            <span>{{ auditCase.practiceSnapshot.name }}</span>
          </div>
          <div class="audit-detail__row">
            <span>Categoría</span>
            <span>{{
              categoryLabel(auditCase.practiceSnapshot.category)
            }}</span>
          </div>
        </div>

        <!-- Gestión del caso -->
        <div class="audit-detail__card">
          <div class="audit-detail__card-title">GESTIÓN</div>
          <div class="audit-detail__row">
            <span>Asignado a</span>
            <div style="display: flex; align-items: center; gap: 8px">
              <span v-if="auditCase.assignedToName">{{
                auditCase.assignedToName
              }}</span>
              <el-button size="mini" type="text" @click="assignDialog = true">
                {{ auditCase.assignedToName ? "Cambiar" : "Asignar" }}
              </el-button>
            </div>
          </div>
          <div class="audit-detail__row">
            <span>Especialidad</span>
            <div style="display: flex; align-items: center; gap: 8px">
              <span v-if="auditCase.specialtyName">{{
                auditCase.specialtyName
              }}</span>
              <el-button
                size="mini"
                type="text"
                @click="specialtyDialog = true"
              >
                {{ auditCase.specialtyName ? "Cambiar" : "Asignar" }}
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Acciones rápidas -->
      <div class="audit-detail__actions">
        <el-button size="small" icon="el-icon-user" @click="goToPatient">
          Ver ficha del paciente
        </el-button>
        <el-button
          size="small"
          icon="el-icon-magic-stick"
          type="primary"
          plain
          @click="aiAuditVisible = true"
        >
          Analizar con IA
        </el-button>
      </div>

      <!-- Órdenes y adjuntos -->
      <div
        class="audit-detail__section"
        v-if="
          auditCase.authorizationCase &&
          auditCase.authorizationCase.attachments &&
          auditCase.authorizationCase.attachments.length
        "
      >
        <div class="audit-detail__section-title">Órdenes y adjuntos</div>
        <div class="audit-detail__attachments">
          <div
            v-for="att in auditCase.authorizationCase.attachments"
            :key="att._id"
            class="audit-detail__attachment"
          >
            <i class="el-icon-document"></i>
            <span class="audit-detail__attachment-name">{{
              att.originalName || "Adjunto"
            }}</span>
            <el-button
              v-if="att.url || att.signedUrl"
              type="text"
              size="mini"
              @click="window.open(att.url || att.signedUrl, '_blank')"
            >
              Ver
            </el-button>
          </div>
        </div>
      </div>

      <!-- Bitácora de dictámenes -->
      <div class="audit-detail__section">
        <div class="audit-detail__section-title">Bitácora de dictámenes</div>
        <div
          v-if="auditCase.dictumLog && auditCase.dictumLog.length"
          class="audit-detail__timeline"
        >
          <div
            v-for="(d, i) in auditCase.dictumLog"
            :key="d._id || i"
            class="audit-detail__timeline-item"
          >
            <div class="audit-detail__timeline-meta">
              <span class="audit-detail__timeline-author">{{
                d.authorName
              }}</span>
              <span class="audit-detail__timeline-date">{{
                d.date | formatDate
              }}</span>
              <span
                class="audit-detail__badge audit-detail__badge--sm"
                :class="statusClass(d.decision)"
              >
                {{ statusLabel(d.decision) }}
              </span>
            </div>
            <div
              v-if="d.diagnosis"
              style="margin-top: 4px; font-size: 12px; color: #6b7294"
            >
              <span style="font-weight: 600">Diagnóstico:</span>
              {{ d.diagnosis.code }} — {{ d.diagnosis.name }}
            </div>
            <div v-if="d.notes" class="audit-detail__timeline-notes">
              {{ d.notes }}
            </div>
            <div v-if="d.requestsMoreInfo" class="audit-detail__timeline-info">
              <i class="el-icon-info"></i> Solicita info: {{ d.infoRequested }}
            </div>
          </div>
        </div>
        <div v-else class="audit-detail__empty">Sin dictámenes aún</div>
      </div>

      <!-- Interconsultas -->
      <div class="audit-detail__section">
        <div
          class="audit-detail__section-title"
          style="
            display: flex;
            justify-content: space-between;
            align-items: center;
          "
        >
          <span>Interconsultas</span>
          <el-button
            size="mini"
            icon="el-icon-plus"
            @click="interconsultDialog = true"
            >Nueva</el-button
          >
        </div>
        <div
          v-if="
            auditCase.interconsultations && auditCase.interconsultations.length
          "
        >
          <div
            v-for="(ic, i) in auditCase.interconsultations"
            :key="ic._id || i"
            class="audit-detail__interconsult"
          >
            <div class="audit-detail__interconsult-header">
              <span class="audit-detail__badge audit-detail__badge--category">{{
                interconsultLabel(ic.type)
              }}</span>
              <span class="audit-detail__timeline-date">{{
                ic.requestedAt | formatDate
              }}</span>
            </div>
            <div class="audit-detail__row">
              <span>Con</span>
              <span>{{
                ic.isExternal ? ic.providerManual : ic.providerName
              }}</span>
            </div>
            <div v-if="ic.notes" class="audit-detail__timeline-notes">
              {{ ic.notes }}
            </div>
          </div>
        </div>
        <div v-else class="audit-detail__empty">Sin interconsultas</div>
      </div>

      <!-- Panel de resolución (solo si no está resuelto) -->
      <div
        v-if="
          auditCase.status !== 'approved' && auditCase.status !== 'rejected'
        "
        class="audit-detail__section audit-detail__resolution"
      >
        <div class="audit-detail__section-title">Agregar dictamen</div>
        <el-form label-position="top" size="small">
          <el-form-item label="Decisión">
            <el-radio-group v-model="dictumForm.decision">
              <el-radio-button label="under_review"
                >En revisión</el-radio-button
              >
              <el-radio-button label="approved">Aprobar</el-radio-button>
              <el-radio-button label="rejected">Rechazar</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <!-- Diagnóstico CIE-10 -->
          <div class="form-item" style="margin-bottom: 16px">
            <div class="form-label">DIAGNÓSTICO CIE-10</div>
            <el-select
              v-model="dictumForm.diagnosis"
              filterable
              remote
              clearable
              placeholder="Buscar por código o descripción..."
              :remote-method="searchDiagnosis"
              :loading="diagnosisLoading"
              style="width: 100%"
            >
              <el-option
                v-for="d in diagnosisOptions"
                :key="d._id"
                :label="`${d.code} — ${d.name}`"
                :value="d._id"
              />
            </el-select>
          </div>

          <el-form-item label="Notas / fundamentación">
            <el-input v-model="dictumForm.notes" type="textarea" :rows="3" />
          </el-form-item>

          <el-form-item v-if="dictumForm.decision === 'under_review'">
            <el-checkbox v-model="dictumForm.requestsMoreInfo"
              >Solicitar más información</el-checkbox
            >
            <el-input
              v-if="dictumForm.requestsMoreInfo"
              v-model="dictumForm.infoRequested"
              placeholder="Detalle qué información se solicita..."
              style="margin-top: 8px"
            />
          </el-form-item>

          <!-- Campos de aprobación -->
          <template v-if="dictumForm.decision === 'approved'">
            <el-form-item label="Prestador aprobado">
              <el-radio-group v-model="dictumForm.approvalScope">
                <el-radio label="free_network">Libre de la red</el-radio>
                <el-radio label="specific_provider"
                  >Prestador específico</el-radio
                >
              </el-radio-group>
            </el-form-item>

            <el-form-item
              v-if="dictumForm.approvalScope === 'specific_provider'"
              label="Prestador"
            >
              <el-select
                v-model="dictumForm.approvedProviderId"
                filterable
                placeholder="Buscar prestador"
                style="width: 100%"
                @change="onProviderSelect"
              >
                <el-option
                  v-for="p in providers"
                  :key="p._id"
                  :label="`${p.firstName} ${p.lastName}`"
                  :value="p._id"
                />
              </el-select>
            </el-form-item>

            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="Vigencia desde">
                  <el-date-picker
                    v-model="dictumForm.approvedFrom"
                    type="date"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Vigencia hasta">
                  <el-date-picker
                    v-model="dictumForm.approvedUntil"
                    type="date"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </template>

          <!-- Rechazo -->
          <el-form-item
            v-if="dictumForm.decision === 'rejected'"
            label="Motivo de rechazo"
          >
            <el-input
              v-model="dictumForm.rejectionReason"
              type="textarea"
              :rows="2"
            />
          </el-form-item>

          <el-button
            type="primary"
            size="small"
            :loading="submitting"
            @click="submitDictum"
          >
            Confirmar dictamen
          </el-button>
        </el-form>
      </div>

      <!-- Resultado final: Aprobado -->
      <div
        v-if="auditCase.status === 'approved'"
        class="audit-detail__section audit-detail__result audit-detail__result--approved"
      >
        <div class="audit-detail__section-title">Dictamen final — Aprobado</div>
        <div class="audit-detail__row">
          <span>Prestador</span>
          <span>{{
            auditCase.approvalScope === "free_network"
              ? "Libre de la red"
              : auditCase.approvedProviderName
          }}</span>
        </div>
        <div class="audit-detail__row">
          <span>Vigencia</span>
          <span
            >{{ auditCase.approvedFrom | formatDate }} —
            {{ auditCase.approvedUntil | formatDate }}</span
          >
        </div>
        <div v-if="auditCase.finalNotes" class="audit-detail__row">
          <span>Notas</span>
          <span>{{ auditCase.finalNotes }}</span>
        </div>
      </div>

      <!-- Resultado final: Rechazado -->
      <div
        v-if="auditCase.status === 'rejected'"
        class="audit-detail__section audit-detail__result audit-detail__result--rejected"
      >
        <div class="audit-detail__section-title">
          Dictamen final — Rechazado
        </div>
        <div class="audit-detail__row">
          <span>Motivo</span>
          <span>{{ auditCase.rejectionReason }}</span>
        </div>
      </div>
    </template>

    <!-- Dialog asignar auditor -->
    <el-dialog
      title="Asignar auditor"
      :visible.sync="assignDialog"
      width="400px"
    >
      <el-select
        v-model="assignAuditorId"
        placeholder="Seleccionar auditor"
        style="width: 100%"
      >
        <el-option
          v-for="a in auditors"
          :key="a._id"
          :label="`${a.firstName} ${a.lastName}`"
          :value="a._id"
        />
      </el-select>
      <span slot="footer">
        <el-button size="small" @click="assignDialog = false"
          >Cancelar</el-button
        >
        <el-button
          size="small"
          type="primary"
          :loading="assigning"
          @click="confirmAssign"
          >Confirmar</el-button
        >
      </span>
    </el-dialog>

    <!-- Dialog asignar especialidad -->
    <el-dialog
      title="Asignar especialidad"
      :visible.sync="specialtyDialog"
      width="400px"
    >
      <el-select
        v-model="specialtyId"
        filterable
        placeholder="Buscar especialidad"
        style="width: 100%"
      >
        <el-option
          v-for="s in specialties"
          :key="s._id"
          :label="s.name"
          :value="s._id"
        />
      </el-select>
      <span slot="footer">
        <el-button size="small" @click="specialtyDialog = false"
          >Cancelar</el-button
        >
        <el-button
          size="small"
          type="primary"
          :loading="savingSpecialty"
          @click="confirmSpecialty"
          >Confirmar</el-button
        >
      </span>
    </el-dialog>

    <!-- Dialog interconsulta -->
    <el-dialog
      title="Nueva interconsulta"
      :visible.sync="interconsultDialog"
      width="500px"
    >
      <el-form label-position="top" size="small">
        <el-form-item label="Tipo">
          <el-select v-model="icForm.type" style="width: 100%">
            <el-option label="Interconsulta médica" value="medical" />
            <el-option
              label="Interconsulta administrativa"
              value="administrative"
            />
            <el-option label="Segunda opinión" value="second_opinion" />
          </el-select>
        </el-form-item>

        <el-form-item label="Prestador / Auditor">
          <el-radio-group
            v-model="icForm.isExternal"
            style="margin-bottom: 8px"
          >
            <el-radio :label="false">De la red</el-radio>
            <el-radio :label="true">Externo (cargar manualmente)</el-radio>
          </el-radio-group>
          <el-select
            v-if="!icForm.isExternal"
            v-model="icForm.providerRef"
            filterable
            placeholder="Buscar en la red"
            style="width: 100%"
            @change="onIcProviderSelect"
          >
            <el-option
              v-for="p in providers"
              :key="p._id"
              :label="`${p.firstName} ${p.lastName}`"
              :value="p._id"
            />
          </el-select>
          <el-input
            v-else
            v-model="icForm.providerManual"
            placeholder="Nombre del profesional externo"
          />
        </el-form-item>

        <el-form-item label="Notas">
          <el-input v-model="icForm.notes" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="interconsultDialog = false"
          >Cancelar</el-button
        >
        <el-button
          size="small"
          type="primary"
          :loading="savingIc"
          @click="confirmInterconsult"
          >Registrar</el-button
        >
      </span>
    </el-dialog>

    <!-- Modal IA -->
    <AIAuditModal
      :visible="aiAuditVisible"
      :auditCaseId="$route.params.id"
      :auditCase="auditCase"
      @close="aiAuditVisible = false"
      @apply-dictum="
        (text) => {
          dictumForm.notes = text;
          aiAuditVisible = false;
        }
      "
    />
  </div>
</template>

<script>
import auditService from "@/services/audit";
import diagnosisService from "@/services/diagnosis";
import providerNetworkService from "@/services/provider-network";
import medicalSpecialtyService from "@/services/medical-specialty";
import AIAuditModal from "@/components/ai/AIAuditModal.vue";
import api from "@/services/api";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component AuditDetail */
export default {
  name: "AuditDetail",
  components: { AIAuditModal },

  filters: {
    /**
     * @description Formatea fecha ISO a dd/MM/yyyy HH:mm
     * @param {string} value
     * @returns {string}
     */
    formatDate(value) {
      if (!value) return "-";
      try {
        return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: es });
      } catch {
        return "-";
      }
    },
  },

  data() {
    return {
      /** @type {Object|null} Caso de auditoría cargado */
      auditCase: null,
      /** @type {boolean} */
      isLoading: false,
      /** @type {Array} */
      auditors: [],
      /** @type {Array} */
      specialties: [],
      /** @type {Array} */
      providers: [],
      /** @type {Object} Formulario de dictamen */
      diagnosisOptions: [],
      diagnosisLoading: false,
      dictumForm: {
        decision: "under_review",
        diagnosis: null,
        diagnosisSearch: "",
        notes: "",
        requestsMoreInfo: false,
        infoRequested: "",
        approvalScope: "free_network",
        approvedProviderId: "",
        approvedProviderName: "",
        approvedFrom: null,
        approvedUntil: null,
        finalNotes: "",
        rejectionReason: "",
      },
      /** @type {boolean} */
      submitting: false,
      /** @type {boolean} */
      assignDialog: false,
      /** @type {string} */
      assignAuditorId: "",
      /** @type {boolean} */
      assigning: false,
      /** @type {boolean} */
      specialtyDialog: false,
      /** @type {string} */
      specialtyId: "",
      /** @type {boolean} */
      savingSpecialty: false,
      /** @type {boolean} */
      interconsultDialog: false,
      /** @type {Object} Formulario de interconsulta */
      icForm: {
        type: "medical",
        isExternal: false,
        providerRef: "",
        providerName: "",
        providerManual: "",
        notes: "",
      },
      /** @type {boolean} */
      savingIc: false,
      /** @type {boolean} */
      aiAuditVisible: false,
    };
  },

  created() {
    this.$bus.$emit("update-title", "Auditoría");
    this.loadCase();
    this.loadAuditors();
    this.loadSpecialties();
    this.loadProviders();
  },

  destroyed() {
    this.$bus.$emit("update-title", undefined);
  },

  methods: {
    /**
     * @description Navega a la ficha del paciente vinculado al afiliado del caso
     */
    async goToPatient() {
      try {
        const memberId = this.auditCase?.memberSnapshot?.memberId;
        if (!memberId) {
          this.$message.warning("No se encontró el número de afiliado");
          return;
        }
        const result = await api
          .get("/members", { params: { searchInput: memberId, limit: 1 } })
          .then((r) => r.data);
        const member = result?.docs?.[0];
        if (!member?.patient) {
          this.$message.warning(
            "El afiliado no tiene historia clínica vinculada"
          );
          return;
        }
        const patientId = member.patient?._id || member.patient;
        this.$router.push({
          name: "patient-dashboard",
          params: { id: patientId },
          query: { back: "audit", auditId: this.$route.params.id },
        });
      } catch (e) {
        this.$message.error("Error al buscar el paciente");
      }
    },

    /** @description Carga el caso de auditoría por ID de la ruta */
    async loadCase() {
      this.isLoading = true;
      try {
        this.auditCase = await auditService.getById(this.$route.params.id);
        this.$bus.$emit(
          "update-title",
          `Auditoría #${this.auditCase.authCaseNumber}`
        );
      } catch (e) {
        this.$message.error("Error al cargar el caso");
      } finally {
        this.isLoading = false;
      }
    },

    /** @description Carga la lista de auditores */
    async loadAuditors() {
      try {
        this.auditors = await auditService.getAuditors();
      } catch (e) {
        /* silencioso */
      }
    },

    /** @description Carga la lista de especialidades médicas */
    async loadSpecialties() {
      try {
        const result = await medicalSpecialtyService.getMedicalSpecialties({
          limit: 100,
        });
        this.specialties = result.docs || result;
      } catch (e) {
        /* silencioso */
      }
    },

    /** @description Carga la lista de prestadores */
    async loadProviders() {
      try {
        const result = await providerNetworkService.query({ limit: 200 });
        this.providers = result.docs || result;
      } catch (e) {
        /* silencioso */
      }
    },

    /** @description Envía un nuevo dictamen al caso */
    async submitDictum() {
      if (!this.dictumForm.decision) return;
      this.submitting = true;
      try {
        await auditService.addDictum(this.auditCase._id, this.dictumForm);
        this.$message.success("Dictamen registrado");
        this.loadCase();
      } catch (e) {
        this.$message.error("Error al registrar dictamen");
      } finally {
        this.submitting = false;
      }
    },

    /** @description Confirma asignación de auditor */
    async confirmAssign() {
      if (!this.assignAuditorId) return;
      this.assigning = true;
      try {
        await auditService.assign(this.auditCase._id, this.assignAuditorId);
        this.$message.success("Auditor asignado");
        this.assignDialog = false;
        this.loadCase();
      } catch (e) {
        this.$message.error("Error al asignar");
      } finally {
        this.assigning = false;
      }
    },

    /** @description Confirma asignación de especialidad */
    async confirmSpecialty() {
      if (!this.specialtyId) return;
      this.savingSpecialty = true;
      try {
        await auditService.setSpecialty(this.auditCase._id, this.specialtyId);
        this.$message.success("Especialidad asignada");
        this.specialtyDialog = false;
        this.loadCase();
      } catch (e) {
        this.$message.error("Error al asignar especialidad");
      } finally {
        this.savingSpecialty = false;
      }
    },

    /** @description Registra una nueva interconsulta */
    async confirmInterconsult() {
      this.savingIc = true;
      try {
        await auditService.addInterconsult(this.auditCase._id, this.icForm);
        this.$message.success("Interconsulta registrada");
        this.interconsultDialog = false;
        this.icForm = {
          type: "medical",
          isExternal: false,
          providerRef: "",
          providerName: "",
          providerManual: "",
          notes: "",
        };
        this.loadCase();
      } catch (e) {
        this.$message.error("Error al registrar interconsulta");
      } finally {
        this.savingIc = false;
      }
    },

    /**
     * @method searchDiagnosis
     * @description Busca diagnósticos CIE-10 por texto en el catálogo maestro
     * @param {string} query - Texto de búsqueda
     */
    async searchDiagnosis(query) {
      if (!query || query.length < 2) return;
      this.diagnosisLoading = true;
      try {
        const results = await diagnosisService.search(query);
        this.diagnosisOptions = Array.isArray(results)
          ? results
          : results.docs || [];
      } catch (e) {
        this.diagnosisOptions = [];
      } finally {
        this.diagnosisLoading = false;
      }
    },

    /**
     * @description Guarda nombre del prestador seleccionado para el dictamen
     * @param {string} id
     */
    onProviderSelect(id) {
      const p = this.providers.find((p) => p._id === id);
      if (p)
        this.dictumForm.approvedProviderName = `${p.firstName} ${p.lastName}`;
    },

    /**
     * @description Guarda nombre del prestador seleccionado para interconsulta
     * @param {string} id
     */
    onIcProviderSelect(id) {
      const p = this.providers.find((p) => p._id === id);
      if (p) this.icForm.providerName = `${p.firstName} ${p.lastName}`;
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusClass(status) {
      return (
        {
          pending: "audit-detail__badge--pending",
          under_review: "audit-detail__badge--review",
          approved: "audit-detail__badge--approved",
          rejected: "audit-detail__badge--rejected",
        }[status] || ""
      );
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusLabel(status) {
      return (
        {
          pending: "Pendiente",
          under_review: "En revisión",
          approved: "Aprobado",
          rejected: "Rechazado",
        }[status] || status
      );
    },

    /**
     * @param {string} category
     * @returns {string}
     */
    categoryLabel(category) {
      return (
        {
          consultation: "Consulta",
          lab: "Laboratorio",
          imaging: "Imágenes",
          surgery: "Cirugía",
          dental: "Odontología",
          mental_health: "Salud mental",
          rehabilitation: "Rehabilitación",
          emergency: "Urgencias",
          hospitalization: "Internación",
          maternity: "Maternidad",
          medication: "Medicación",
          supply: "Insumos",
          prosthesis: "Prótesis",
          other: "Otros",
        }[category] ||
        category ||
        "-"
      );
    },

    /**
     * @param {string} type
     * @returns {string}
     */
    interconsultLabel(type) {
      return (
        {
          medical: "Médica",
          administrative: "Administrativa",
          second_opinion: "Segunda opinión",
        }[type] || type
      );
    },
  },
};
</script>

<style scoped>
.audit-detail {
  font-family: "Plus Jakarta Sans", sans-serif;
  max-width: 900px;
}

.audit-detail__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
  cursor: pointer;
  margin-bottom: 20px;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.audit-detail__back:hover {
  opacity: 1;
}

.audit-detail__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.audit-detail__header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.audit-detail__case-num {
  font-size: 22px;
  font-weight: 700;
  color: #002d5e;
}

.audit-detail__date {
  font-size: 13px;
  color: #94a3b8;
}

.audit-detail__cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.audit-detail__card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.audit-detail__card-title {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
}

.audit-detail__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 13px;
  margin-bottom: 8px;
  gap: 8px;
}
.audit-detail__row span:first-child {
  color: #94a3b8;
  white-space: nowrap;
}
.audit-detail__row strong {
  color: #1a1f36;
  text-align: right;
}

.audit-detail__plan {
  color: #05e5ba;
  font-weight: 600;
}

.audit-detail__code {
  color: #05e5ba;
  font-weight: 700;
}

.audit-detail__section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.audit-detail__section-title {
  font-size: 13px;
  font-weight: 700;
  color: #002d5e;
  margin-bottom: 16px;
  letter-spacing: 0.04em;
}

.audit-detail__timeline-item {
  border-left: 2px solid #e2e8f0;
  padding-left: 16px;
  margin-bottom: 16px;
}

.audit-detail__timeline-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.audit-detail__timeline-author {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

.audit-detail__timeline-date {
  font-size: 12px;
  color: #94a3b8;
}

.audit-detail__timeline-notes {
  font-size: 13px;
  color: #6b7294;
}

.audit-detail__timeline-info {
  font-size: 12px;
  color: #185fa5;
  margin-top: 4px;
}

.audit-detail__interconsult {
  border: 1px solid #f0f2f7;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.audit-detail__interconsult-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.audit-detail__empty {
  font-size: 13px;
  color: #94a3b8;
}

.audit-detail__resolution {
  border-color: #e2e8f0;
}

.audit-detail__result--approved {
  border-color: #c0dd97;
  background: #f6fbee;
}

.audit-detail__result--rejected {
  border-color: #f7c1c1;
  background: #fff8f8;
}

.audit-detail__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.audit-detail__badge--sm {
  padding: 2px 8px;
  font-size: 10px;
}

.audit-detail__badge--category {
  background: #eef1f6;
  color: #002d5e;
}

.audit-detail__badge--pending {
  background: #faeeda;
  color: #854f0b;
}

.audit-detail__badge--review {
  background: #e6f1fb;
  color: #0c447c;
}

.audit-detail__badge--approved {
  background: #eaf3de;
  color: #3b6d11;
}

.audit-detail__badge--rejected {
  background: #fcebeb;
  color: #a32d2d;
}

.audit-detail__actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.audit-detail__attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audit-detail__attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #334155;
}

.audit-detail__attachment i {
  color: #94a3b8;
}

.audit-detail__attachment-name {
  flex: 1;
}
</style>
